import React from 'react';

/**
 * Shared modal dialog lifecycle (W3C APG "Dialog (Modal)" pattern).
 *
 * One primitive owns, for every modal surface (confirmation dialogs, form modals,
 * side drawers):
 *  - the accessible name/description wiring (`DialogTitle` / `DialogDescription`),
 *  - initial focus (explicit ref, `[data-autofocus]`, first tabbable, else the dialog),
 *  - Tab/Shift+Tab containment that skips disabled and hidden controls and still works
 *    when nothing inside is focusable,
 *  - Escape / backdrop dismissal rules (`dismissible`, `closeOnBackdrop`),
 *  - background isolation (`inert` on everything outside the dialog) and scroll lock,
 *  - focus restoration that survives a removed trigger (and a trigger that is no longer rendered),
 *  - focus recovery when a control inside disables itself while the dialog is busy (and its return when work settles),
 *  - a global stack so only the top-most of several open dialogs reacts to keys.
 *
 * The dialog renders in place (not in a portal) so a dialog mounted inside a host
 * `<form>` keeps its DOM relationship with that form. Elements marked
 * `data-dialog-persistent` (for example the toast live region) stay interactive.
 */

const TABBABLE_SELECTOR =
  'a[href], area[href], button, input, select, textarea, summary, iframe, audio[controls], video[controls], [contenteditable]:not([contenteditable="false"]), [tabindex]';

function isRendered(element: HTMLElement, root: HTMLElement): boolean {
  let node: HTMLElement | null = element;
  while (node) {
    if (node.hidden || node.hasAttribute('inert')) return false;
    if (window.getComputedStyle(node).display === 'none') return false;
    if (node === root) break;
    node = node.parentElement;
  }
  const visibility = window.getComputedStyle(element).visibility;
  return visibility !== 'hidden' && visibility !== 'collapse';
}

/** Keyboard-reachable descendants of `root`, in DOM order, excluding disabled and hidden controls. */
export function getDialogTabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter((element) => {
    if (element.tabIndex < 0) return false;
    if (element.matches(':disabled')) return false;
    if (element instanceof HTMLInputElement && element.type === 'hidden') return false;
    return isRendered(element, root);
  });
}

interface StackEntry {
  root: () => HTMLElement | null;
  canDismiss: () => boolean;
  requestClose: () => void;
  closeOnEscape: () => boolean;
}

const stack: StackEntry[] = [];
const isolated = new Map<Element, string | null>();

function releaseIsolation(): void {
  isolated.forEach((previous, element) => {
    if (previous === null) element.removeAttribute('inert');
    else element.setAttribute('inert', previous);
  });
  isolated.clear();
}

const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT']);

function applyIsolation(): void {
  releaseIsolation();
  const top = stack[stack.length - 1]?.root();
  if (!top) return;
  let node: HTMLElement = top;
  while (node.parentElement) {
    const parent: HTMLElement = node.parentElement;
    for (const sibling of Array.from(parent.children)) {
      if (sibling === node || SKIPPED_TAGS.has(sibling.tagName)) continue;
      if (sibling.hasAttribute('data-dialog-persistent')) continue;
      isolated.set(sibling, sibling.getAttribute('inert'));
      sibling.setAttribute('inert', '');
    }
    if (parent === document.body) break;
    node = parent;
  }
}

function handleDocumentKeyDown(event: KeyboardEvent): void {
  const top = stack[stack.length - 1];
  const root = top?.root();
  if (!top || !root || event.defaultPrevented || event.isComposing) return;

  if (event.key === 'Escape') {
    if (top.closeOnEscape() && top.canDismiss()) {
      event.preventDefault();
      top.requestClose();
    }
    return;
  }

  if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
  const tabbables = getDialogTabbables(root);
  if (!tabbables.length) {
    event.preventDefault();
    root.focus();
    return;
  }
  const first = tabbables[0];
  const last = tabbables[tabbables.length - 1];
  const active = document.activeElement;
  if (!active || !root.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  if (event.shiftKey) {
    const beforeFirst = active === root || active === first || Boolean(first.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_PRECEDING);
    if (beforeFirst) {
      event.preventDefault();
      last.focus();
    }
  } else {
    const afterLast = active === last || Boolean(last.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_FOLLOWING);
    if (afterLast) {
      event.preventDefault();
      first.focus();
    }
  }
}

function registerDialog(entry: StackEntry): () => void {
  if (!stack.length) document.addEventListener('keydown', handleDocumentKeyDown);
  stack.push(entry);
  applyIsolation();
  return () => {
    const index = stack.indexOf(entry);
    if (index >= 0) stack.splice(index, 1);
    if (!stack.length) document.removeEventListener('keydown', handleDocumentKeyDown);
    applyIsolation();
  };
}

interface ScrollLockRecord { count: number; overflow: string; paddingRight: string }
const scrollLocks = new Map<HTMLElement, ScrollLockRecord>();

function lockScroll(root: HTMLElement): () => void {
  const targets: HTMLElement[] = [document.documentElement, document.body];
  for (let node = root.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(`${style.overflowY} ${style.overflowX}`)) targets.push(node);
  }
  const viewportWidth = document.documentElement.clientWidth;
  const scrollbar = viewportWidth > 0 ? Math.max(0, window.innerWidth - viewportWidth) : 0;
  for (const target of targets) {
    const existing = scrollLocks.get(target);
    if (existing) {
      existing.count += 1;
      continue;
    }
    scrollLocks.set(target, { count: 1, overflow: target.style.overflow, paddingRight: target.style.paddingRight });
    target.style.overflow = 'hidden';
    if (target === document.body && scrollbar > 0) {
      const current = Number.parseFloat(window.getComputedStyle(target).paddingRight) || 0;
      target.style.paddingRight = `${current + scrollbar}px`;
    }
  }
  return () => {
    for (const target of targets) {
      const record = scrollLocks.get(target);
      if (!record) continue;
      record.count -= 1;
      if (record.count > 0) continue;
      target.style.overflow = record.overflow;
      target.style.paddingRight = record.paddingRight;
      scrollLocks.delete(target);
    }
  };
}

/** False when the element or any ancestor is `display: none`, `hidden` or `inert` (so it cannot take focus). */
function isDisplayed(element: HTMLElement): boolean {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (node.hidden || node.hasAttribute('inert')) return false;
    if (window.getComputedStyle(node).display === 'none') return false;
  }
  return true;
}

function isFocusable(target: HTMLElement | null | undefined): target is HTMLElement {
  return Boolean(target && target.isConnected && !target.matches(':disabled') && isDisplayed(target));
}

function focusLandmarkFallback(): void {
  const landmark = document.querySelector<HTMLElement>('main, [role="main"]');
  if (!landmark) return;
  if (landmark.tabIndex < 0 && !landmark.hasAttribute('tabindex')) {
    landmark.setAttribute('tabindex', '-1');
    landmark.addEventListener('blur', () => landmark.removeAttribute('tabindex'), { once: true });
  }
  landmark.focus({ preventScroll: true });
}

interface DialogContextValue {
  titleId: string;
  descriptionId: string;
  setHasTitle: (value: boolean) => void;
  setHasDescription: (value: boolean) => void;
}

const DialogContext = React.createContext<DialogContextValue | null>(null);

function useDialogContext(component: string): DialogContextValue {
  const context = React.useContext(DialogContext);
  if (!context) throw new Error(`${component} must be rendered inside a Dialog`);
  return context;
}

export interface DialogProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role' | 'onKeyDown' | 'aria-modal'> {
  open: boolean;
  /** Called for Escape and backdrop dismissal when the dialog is dismissible. Not called while `dismissible` is false. */
  onClose: () => void;
  role?: 'dialog' | 'alertdialog';
  /** When false (for example while a mutation is pending) Escape and backdrop clicks are ignored. Defaults to true. */
  dismissible?: boolean;
  closeOnEscape?: boolean;
  /** Data-entry dialogs should set this to false so a stray click cannot discard typed values. Defaults to true. */
  closeOnBackdrop?: boolean;
  /** Marks the dialog as busy and returns focus into it if the focused control becomes disabled. */
  busy?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Preferred element to refocus on close; falls back to the previously focused element, then `returnFocusFallback`, then the main landmark. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Resolved on close when neither `returnFocusRef` nor the previously focused element can take focus (for example the
   * trigger was removed or is in a layout that is not displayed). Return the element that now stands in for the trigger.
   */
  returnFocusFallback?: () => HTMLElement | null | undefined;
  /**
   * Ids of elements that belong to the accessible name in front of the `DialogTitle` (for example an eyebrow label).
   * Only used when `aria-labelledby` and `aria-label` are not given.
   */
  labelledByPrefix?: string;
  backdropClassName?: string;
  children: React.ReactNode;
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  role = 'dialog',
  dismissible = true,
  closeOnEscape = true,
  closeOnBackdrop = true,
  busy = false,
  initialFocusRef,
  returnFocusRef,
  returnFocusFallback,
  labelledByPrefix,
  backdropClassName = 'absolute inset-0 bg-black/60 transition-opacity',
  className = '',
  children,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  ...rest
}) => {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [hasTitle, setHasTitle] = React.useState(false);
  const [hasDescription, setHasDescription] = React.useState(false);
  const lastFocusedRef = React.useRef<HTMLElement | null>(null);
  const busyOriginRef = React.useRef<HTMLElement | null>(null);
  const wasBusyRef = React.useRef(false);
  const latest = React.useRef({ onClose, dismissible, closeOnEscape, initialFocusRef, returnFocusRef, returnFocusFallback });
  latest.current = { onClose, dismissible, closeOnEscape, initialFocusRef, returnFocusRef, returnFocusFallback };

  React.useEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    if (!root) return;
    const active = document.activeElement;
    const previouslyFocused = active instanceof HTMLElement && active !== document.body && !root.contains(active) ? active : null;
    const unregister = registerDialog({
      root: () => rootRef.current,
      canDismiss: () => latest.current.dismissible,
      closeOnEscape: () => latest.current.closeOnEscape,
      requestClose: () => latest.current.onClose(),
    });
    const unlockScroll = lockScroll(root);
    // The last control focused inside the dialog: once it disables itself `document.activeElement` no longer names it.
    const onFocusIn = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && event.target !== root) lastFocusedRef.current = event.target;
    };
    root.addEventListener('focusin', onFocusIn);
    const initial = latest.current.initialFocusRef?.current ?? root.querySelector<HTMLElement>('[data-autofocus]') ?? getDialogTabbables(root)[0] ?? root;
    initial.focus({ preventScroll: true });

    return () => {
      root.removeEventListener('focusin', onFocusIn);
      lastFocusedRef.current = null;
      busyOriginRef.current = null;
      wasBusyRef.current = false;
      unregister();
      unlockScroll();
      const current = document.activeElement;
      const focusIsLost = !current || current === document.body || root.contains(current);
      if (!focusIsLost) return;
      const preferred = latest.current.returnFocusRef?.current;
      const target = [preferred, previouslyFocused, latest.current.returnFocusFallback?.()].find(isFocusable);
      if (target) target.focus();
      else focusLandmarkFallback();
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    if (!root) return;
    const active = document.activeElement;
    const focusLost = !active || active === document.body || !root.contains(active) || (active as HTMLElement).matches?.(':disabled');
    if (busy) {
      if (!wasBusyRef.current) busyOriginRef.current = lastFocusedRef.current;
      wasBusyRef.current = true;
      if (focusLost) root.focus({ preventScroll: true });
      return;
    }
    if (!wasBusyRef.current) return;
    wasBusyRef.current = false;
    const origin = busyOriginRef.current;
    busyOriginRef.current = null;
    // Work settled: give focus back to the control it was taken from, unless the user has already moved it to a live control.
    if (!focusLost && active !== root) return;
    const target = isFocusable(origin) && root.contains(origin) ? origin : getDialogTabbables(root)[0];
    target?.focus({ preventScroll: true });
  }, [open, busy]);

  const context = React.useMemo<DialogContextValue>(() => ({ titleId, descriptionId, setHasTitle, setHasDescription }), [titleId, descriptionId]);
  if (!open) return null;

  return (
    <DialogContext.Provider value={context}>
      <div
        {...rest}
        ref={rootRef}
        role={role}
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy ?? (!ariaLabel && hasTitle ? (labelledByPrefix ? `${labelledByPrefix} ${titleId}` : titleId) : undefined)}
        aria-describedby={ariaDescribedBy ?? (hasDescription ? descriptionId : undefined)}
        aria-busy={busy || undefined}
        tabIndex={-1}
        data-dialog=""
        className={`fixed inset-0 outline-none ${className}`}
      >
        <div
          aria-hidden="true"
          data-dialog-backdrop=""
          className={backdropClassName}
          onClick={() => {
            if (closeOnBackdrop && dismissible) onClose();
          }}
        />
        {children}
      </div>
    </DialogContext.Provider>
  );
};

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4';
export interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> { as?: HeadingTag }

/** The visible heading that names the surrounding Dialog. Render exactly one per dialog. */
export const DialogTitle: React.FC<DialogTitleProps> = ({ as: Tag = 'h2', ...props }) => {
  const { titleId, setHasTitle } = useDialogContext('DialogTitle');
  React.useLayoutEffect(() => {
    setHasTitle(true);
    return () => setHasTitle(false);
  }, [setHasTitle]);
  return <Tag {...props} id={titleId} />;
};

/** Optional descriptive text exposed as the Dialog's accessible description. */
export const DialogDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = (props) => {
  const { descriptionId, setHasDescription } = useDialogContext('DialogDescription');
  React.useLayoutEffect(() => {
    setHasDescription(true);
    return () => setHasDescription(false);
  }, [setHasDescription]);
  return <p {...props} id={descriptionId} />;
};
